import { Component, computed, inject, input, output } from '@angular/core';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { CardUIType, LMS_PLATFORM_LABELS, PlaylistCardShort } from '@app/app/shared/models';
import { LucideCheck, LucideLock } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'ptl-card-item-main-view',
  templateUrl: './card-item-main-view.component.html',
  styleUrls: ['./card-item-main-view.component.scss'],
})
export class CardItemMainViewComponent {
  private readonly transloco = inject(TranslocoService);

  protected readonly LucideLock = LucideLock;
  protected readonly LucideCheck = LucideCheck;

  card = input<PlaylistCardShort>();
  cardIndex = input<number>(0);
  cardType = input<CardUIType>('general');
  showImage = input(true);
  showMeta = input(true);
  showReviews = input(true);

  cardClick = output<PlaylistCardShort>();

  private readonly CARD_GRADIENTS = [
    'linear-gradient(135deg, #ede9fe, #c4b5fd)',
    'linear-gradient(135deg, #eff6ff, #bfdbfe)',
    'linear-gradient(135deg, #ecfdf5, #6ee7b7)',
    'linear-gradient(135deg, #f5f3ff, #ddd6fe)',
    'linear-gradient(135deg, #fef9c3, #fde68a)',
    'linear-gradient(135deg, #fff1f2, #fecdd3)',
  ];

  gradient = computed(() => this.CARD_GRADIENTS[this.cardIndex() % this.CARD_GRADIENTS.length]);

  cardSubheader = computed(() => {
    const card = this.card();
    return card?.subHeader ? LanguageCodeHelper.getDataByLanguageCode(card.subHeader).value.trim() : '';
  });

  lmsPlatformLabel = computed(() => {
    const platformType = this.card()?.lmsPlatformType;
    return platformType ? LMS_PLATFORM_LABELS[platformType] || platformType : '';
  });

  typeDisplayName = computed(() => {
    const type = this.card()?.v3CardType || this.card()?.collectionType;
    if (!type) return '';
    if (type.toUpperCase() === 'LTI_COURSE') {
      const platformType = this.card()?.lmsPlatformType;
      if (platformType === 'CANVAS') {
        return this.transloco.translate('translations.cardItem.type.CANVAS_COURSE');
      }
      return this.transloco.translate('translations.cardItem.type.LTI_COURSE');
    }
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  });

  typeColorClass = computed(() => {
    const type = this.card()?.v3CardType || this.card()?.collectionType;
    if (!type) return '';
    const t = type.toUpperCase();
    if (t === 'COURSE' || t === 'STANDARD') return 'type-course';
    if (t === 'EVENT') return 'type-event';
    if (t === 'MODULE' || t === 'PROGRAM') return 'type-module';
    return 'type-default';
  });

  progressPct = computed(() => {
    const c = this.card();
    if (c?.completed) return 100;
    const completed = c?.completedItemsCount ?? 0;
    const total = c?.totalItemsCount ?? 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  });

  timeText = computed(() => {
    return null;
  });

  activitiesText = computed(() => {
    const total = this.card()?.totalItemsCount;
    if (!total) return null;
    return `${total} Activities`;
  });

  onCardClick(): void {
    const card = this.card();
    if (card?.lmsCourseUrl) {
      window.open(card.lmsCourseUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    this.cardClick.emit(card);
  }
}
